import { Component, Input, Output, EventEmitter, NgModule, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxPopupModule, DxFormModule, DxButtonModule, DxScrollViewModule } from 'devextreme-angular';
import { PharmacyService } from 'src/app/shared/services/user/pharmacy.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { ScreenService } from 'src/app/shared/services/screen.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { AddressVerificationModule, AddressVerificationResult } from '../address-verification/address-verification.component';

@Component({
  selector: 'app-edit-facility-modal',
  templateUrl: './edit-facility-modal.component.html',
  styleUrls: ['./edit-facility-modal.component.scss']
})
export class EditFacilityModalComponent {
  @Input() visible: boolean = false;
  @Input() facilityId: number;
  @Input() formData: any = {
    name: '',
    delivery_address: {
      line_1: '',
      line_2: '',
      city: '',
      state: '',
      postal_code: ''
    }
  };

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onUpdate = new EventEmitter<any>();

  @ViewChild('av', { static: false }) av: any;

  /** Flipped to true to trigger the verification child component. */
  triggerVerify: boolean = false;
  addressVerified: boolean = false;

  constructor(
    private pharmacyService: PharmacyService,
    private notificationService: NotificationService,
    private screenService: ScreenService,
    private sessionService: SessionService
  ) { }

  get isMobile() {
    return this.screenService.sizes['screen-x-small'] || this.screenService.sizes['screen-small'];
  }

  closeModal() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.triggerVerify = false;
    this.addressVerified = false;
  }

  /** Called whenever an address field changes — invalidates a prior verification. */
  onAddressFieldChange = () => {
    this.addressVerified = false;
  }

  saveChanges() {
    if (!this._validateForm()) return;

    if (!this.addressVerified) {
      // Kick off verification via the child component
      this.triggerVerify = true;
      return;
    }

    this._doSave();
  }

  /** Receives the result from <app-address-verification> */
  onVerificationResult(result: AddressVerificationResult) {
    this.triggerVerify = false;

    switch (result.status) {
      case 'verified':
      case 'skipped':
        this.addressVerified = true;
        this._doSave();
        break;

      case 'accepted':
        // Apply the USPS-corrected address back into the form
        const addr = this.formData.delivery_address;
        const corrected = result.address;
        addr.line_1 = corrected.line_1 || addr.line_1;
        addr.line_2 = corrected.line_2 ?? addr.line_2;
        addr.city = corrected.city || addr.city;
        addr.state = corrected.state || addr.state;
        addr.postal_code = corrected.postal_code || addr.postal_code;
        this.addressVerified = true;
        this._doSave();
        break;

      // 'idle' — child reset itself (user clicked "Edit Address"), do nothing
    }
  }

  private _validateForm(): boolean {
    const addr = this.formData.delivery_address;
    if (!this.formData.name || !addr.line_1 || !addr.city || !addr.postal_code) {
      this.notificationService.showNotify('Please fill all required fields', 'error');
      return false;
    }
    return true;
  }

  private _doSave() {
    const payload = {
      name: this.formData.name,
      delivery_address: {
        line_1: this.formData.delivery_address.line_1,
        line_2: this.formData.delivery_address.line_2,
        city: this.formData.delivery_address.city,
        state: this.formData.delivery_address.state,
        postal_code: this.formData.delivery_address.postal_code
      }
    };

    this.pharmacyService.updatePharmacy(this.facilityId, payload).subscribe(
      () => {
        this.notificationService.showNotify('Facility updated successfully', 'success');
        this.sessionService.primaryFacilityUpdatedTrigger.next(true);
        this.onUpdate.emit({ id: this.facilityId, ...payload });
        this.closeModal();
      },
      error => {
        const errorMsg = error || 'Error updating facility';
        this.notificationService.showNotify(errorMsg, 'error');
      }
    );
  }
}

@NgModule({
  declarations: [EditFacilityModalComponent],
  imports: [CommonModule, DxPopupModule, DxFormModule, DxButtonModule, DxScrollViewModule, AddressVerificationModule],
  exports: [EditFacilityModalComponent]
})
export class EditFacilityModalModule { }
